import { useState, useRef, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import { message } from 'antd';
import Utils from '@utils/utils'
import ImgComparer from '../imgComparer'
import { AppContext } from '@utils/AppContext';
import { AIEditContext } from '../../AIEditContext';
import { IMAGE_TYPE } from '@utils/CONST'
import API from "@api/api";
import eventBus from '@utils/eventBus';
import ImageFinerCanvas from './imageFinerCanvas';
import styles from '../fillLight/index.module.scss'

const ImageFiner = forwardRef((props, ref) => {
    const {
        getImgWithWatermark,
        saveResultToProject,
        setAIEditLoading,
        AIEditLoading
    } = useContext(AIEditContext)
    const {
        AIEditTemprojectId,
        setAIEditTempProjectId,
        setSelectedTaskListId,
        setGlobalTaskReadFlag,
        AIEditRecentTaskId
    } = useContext(AppContext)

    const isMounted = useRef(true)
    const wrapRef = useRef()
    const dragScaleRef = useRef()
    const canvasControlRef = useRef(null)
    const taskIDRef = useRef()
    const imgResultRef = useRef()
    const downloadUrl = useRef()
    const imgWidthRef = useRef()
    const imgHeightRef = useRef()

    const [orignPic, setOrignPic] = useState()
    const [compareBtn, setCompareBtn] = useState(false)
    const [reswidth, setWidth] = useState()
    const [resheight, setHeight] = useState()
    const [displayScale, setDisplayScale] = useState(1)
    const [imgResultWithWatermark, setImgResultWithWatermark] = useState()
    const [imgResult, setImgResult] = useState()
    const [inited, setInited] = useState(false)
    const [canvasRef, setCanvasRef] = useState(null)
    const [canvasRefMouse, setCanvasRefMouse] = useState(null)
    const [moveable, setMoveable] = useState()

    useImperativeHandle(ref, () => ({
        onChange: () => {
            return imgResultRef.current || props.url
        },
        saveResult,
        createClick
    }))

    const calcCanvasSize = () => {
        if (!imgWidthRef.current || !imgHeightRef.current) return
        const containerWidth = wrapRef.current?.clientWidth - 120 - 150
        const containerHeight = wrapRef.current?.clientHeight
        const size = Utils.getImgMaxSizeInContainer(
            imgWidthRef.current,
            imgHeightRef.current,
            containerWidth,
            containerHeight
        )
        setDisplayScale(size.width / imgWidthRef.current)
        setWidth(size.width)
        setHeight(size.height)
    }

    const updateCanvasSizeByImage = async (imageUrl) => {
        if (!imageUrl) return
        const { width, height } = await Utils.getImageDimensions(imageUrl)
        imgWidthRef.current = width
        imgHeightRef.current = height
        calcCanvasSize()
    }

    useEffect(() => {
        const onWindowResize = () => {
            calcCanvasSize()
        }
        window.addEventListener('resize', onWindowResize)
        return () => {
            isMounted.current = false
            window.removeEventListener('resize', onWindowResize)
        }
    }, [])

    useEffect(() => {
        if (props.url) {
            setOrignPic(props.url)
        }
    }, [props.url])

    useEffect(() => {
        if (!orignPic) return
        updateCanvasSizeByImage(orignPic).then(() => {
            setInited(true)
        })
    }, [orignPic])

    const createClick = async () => {
        if (!props.url) return
        const status = Utils.checkLoginStatus(() => createClick())
        if (!status) return

        setAIEditLoading(true)
        try {
            const res = await API.doImageFinerv2({
                maskByType: {
                    main: {
                        image: props.url,
                    }
                },
                num: 1,
                projectId: AIEditTemprojectId || '',
            })
            setGlobalTaskReadFlag(prev => !prev)
            if (!res) {
                setAIEditLoading(false)
                return
            }
            eventBus.emit('updatePower')

            const taskId = res.data?.taskList?.[0]
            setSelectedTaskListId(res.data?.id)
            setAIEditTempProjectId(res.data?.id)

            const pollTaskStatus = async () => {
                if (!isMounted.current) return
                const taskInfo = await API.getTaskInfoV2({ taskId })
                const status = taskInfo.data?.task?.status

                if (status !== 'succeeded' && status !== 'failed') {
                    setTimeout(() => {
                        pollTaskStatus()
                    }, 1000)
                } else if (status === 'succeeded') {
                    eventBus.emit('updatePower')
                    taskIDRef.current = taskInfo.data?.task?.taskId
                    const noWaterImage = await API.commonDownload({ taskId: taskInfo.data?.task?.taskId })
                    if (!noWaterImage) return
                    message.success('处理成功', 5)
                    await updateCanvasSizeByImage(noWaterImage.data)
                    imgResultRef.current = noWaterImage.data
                    setImgResult(noWaterImage.data)
                    setCompareBtn(false)
                    downloadUrl.current = null
                    setAIEditLoading(false)
                } else if (status === 'failed') {
                    eventBus.emit('updatePower')
                    message.error('处理失败', 5)
                    setAIEditLoading(false)
                }
            }
            pollTaskStatus()
        } catch (err) {
            message.warning(err.message)
            setAIEditLoading(false)
        }
    }

    const resetClick = () => {
        setCompareBtn(false)
        imgResultRef.current = null
        setImgResult()
        downloadUrl.current = null
        updateCanvasSizeByImage(props.url)
        canvasControlRef.current?.updateResult(props.url)
        dragScaleRef.current?.resetCanvas()
    }

    const compareClick = () => {
        if (!imgResult) return
        if (!compareBtn) {
            getImgWithWatermark(imgResult).then(base64 => {
                setImgResultWithWatermark(base64)
                setCompareBtn(true)
            })
        } else {
            setCompareBtn(false)
        }
    }

    async function commonDownloadFunc() {
        const status = Utils.checkLoginStatus(() => commonDownloadFunc())
        if (!status) return

        try {
            let res = await API.commonDownload({ taskId: taskIDRef.current || AIEditRecentTaskId })
            if (res?.code != 0) throw new Error(res?.message)
            const image = res.data
            Utils.downUrl(image, 'aiEdit')
            message.success('下载成功');
        } catch (err) {
            message.error(err.message)
        }
    }

    const saveResult = async () => {
        const status = Utils.checkLoginStatus(() => saveResult())
        if (!status) return
        if (!imgResult) {
            message.warning('请先执行超清')
            return
        }
        setAIEditLoading(true)
        const res = await saveResultToProject(props.url, imgResult, AIEditTemprojectId, IMAGE_TYPE.IMAGE_FINER)
        setAIEditLoading(false)
        if (!res) return
        const projectId = res.id
        setSelectedTaskListId(projectId)
        setAIEditTempProjectId(projectId)
        setGlobalTaskReadFlag(pre => !pre)
    }

    const getCanvasRefsFun = (mainCanvasRef, overlayCanvasRef) => {
        setCanvasRef(mainCanvasRef)
        setCanvasRefMouse(overlayCanvasRef)
    }

    const onDragAndScaleChange = ({ moveable }) => {
        setMoveable(moveable)
    }

    useEffect(() => {
        canvasControlRef.current?.updateResult(imgResult || props.url)
    }, [imgResult, props.url])

    useEffect(() => {
        if (!props.onBottomConfigChange) return
        props.onBottomConfigChange(
            inited ? {
                compare: { disabled: !imgResult, trigger: compareClick },
                reset: { disabled: !imgResult, trigger: resetClick },
                download: { disabled: !imgResult && !AIEditRecentTaskId, trigger: commonDownloadFunc },
                remove: { disabled: false, trigger: props.handleCleanClick },
                uploadCb: props.uploadCb,
                scale: canvasRef ? {
                    ref: dragScaleRef,
                    min: 1,
                    max: 8,
                    onChange: onDragAndScaleChange,
                    canvasList: canvasRefMouse ? [canvasRef, canvasRefMouse] : [canvasRef],
                    disabled: compareBtn ? true : false,
                    options: [
                        { label: '适应屏幕', value: 1 },
                        { label: '100%', value: 1 },
                        { label: '200%', value: 2 },
                        { label: '300%', value: 3 },
                        { label: '400%', value: 4 },
                    ]
                } : null
            } : null
        )
        return () => {
            props.onBottomConfigChange(null)
        }
    }, [inited, compareBtn, imgResult, AIEditRecentTaskId, canvasRefMouse, canvasRef, props.uploadCb])

    useEffect(() => {
        if (compareBtn) {
            dragScaleRef.current?.resetCanvas()
        }
    }, [compareBtn])

    const loadingBox = (
        <div className={styles.loadingBox}>
            <img src="https://photostudio-ai-oss.arcsoft.com.cn/photostudio-web/admin/images_v3/loadingBlue.gif" alt="" />
            <p>云端处理中，马上好</p>
        </div>
    )

    const rightContent = (
        <div className={styles.aiBeautyContent_} style={{ position: 'relative' }} ref={wrapRef}>
            {
                inited ?
                    <div className={styles.aiBeautyContent_Item}>
                        <div className={styles.aiBeautyContent_Scale}></div>
                        {
                            compareBtn ?
                                <div style={{ height: resheight, width: reswidth, zIndex: 9999 }}>
                                    <ImgComparer
                                        leftImage={orignPic}
                                        rightImage={imgResultWithWatermark}
                                    ></ImgComparer>
                                </div> : null
                        }
                        <ImageFinerCanvas
                            opacity={compareBtn ? 0 : 1}
                            ref={canvasControlRef}
                            orignPic={orignPic}
                            resultImage={imgResult}
                            reswidth={reswidth}
                            resheight={resheight}
                            displayScale={displayScale}
                            getCanvasRefsFun={getCanvasRefsFun}
                            moveable={moveable}
                        />
                    </div> : loadingBox
            }
            {
                AIEditLoading && loadingBox
            }
        </div>
    )

    return (
        <div className={styles.aiBeauty}>
            <div
                className={styles.aiBeautyContent}
                style={{ borderRadius: inited ? '0 0 16px 16px' : '16px' }}
            >
                {
                    !props.url ?
                        props.defaultPage :
                        rightContent
                }
            </div>
        </div>
    )
})

export default ImageFiner;
